import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaClock,
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperPlane,
} from "react-icons/fa";
import "./Contact.css";
import { useLocalContext } from "../../context/LocalContext";

const Contact = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = {
    name: webinfo.name || "Digitalcoreweb",
    email: webinfo.email || "contact@digitalcoreweb.com",
    phone: webinfo.phone || "+91 80000 00000",
    phonecall: webinfo.phonecall || "+918000000000",
    address: webinfo.address || "India · Global Remote Partnerships",
    whatsapp: webinfo.whatsapp || "https://wa.me/918000000000",
    hours: webinfo.hours || "Mon–Sat · 10am–7pm IST",
  };

  const topics = useMemo(
    () => [
      "Website / High-Converting Landing Page",
      "UI/UX Redesign & Modernization",
      "Technical SEO & Search Dominance",
      "Paid Performance & Lead Ads (PPC/Meta)",
      "Long-Term Growth Collaboration",
      "Other Inquiries",
    ],
    []
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    topic: "",
    consent: false,
    honey: "",
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    setStatus({ type: "", message: "" });
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (form.honey) return;

    if (!form.name || !form.email || !form.message || !form.consent) {
      setStatus({
        type: "error",
        message: "Please complete all required fields and accept the contact consent.",
      });
      return;
    }

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStatus({
        type: "success",
        message: "Message received! Our team will analyze your requirements and reply within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        message: "",
        topic: "",
        consent: false,
        honey: "",
      });
    }, 600);
  };

  return (
    <section className="contactv6" aria-label="Contact section">
      <div className="cv6-wrap">
        <motion.header
          className="cv6-head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="cv6-eyebrow">Get in Touch</span>
          <h1>Let's Engineer Your Next Growth Phase</h1>
          <p>
            Tell us about your brand goals, target timeline, and challenges.
            We will review your inquiry and schedule a strategic consultation.
          </p>
        </motion.header>

        <div className="cv6-grid">
          {/* LEFT: FORM CARD */}
          <motion.div
            className="cv6-card cv6-form-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cv6-form-header">
              <h2>Send a Project Inquiry</h2>
              <p>Complete the form below to receive a bespoke evaluation.</p>
            </div>

            <form onSubmit={submit} noValidate>
              <input
                type="text"
                name="honey"
                value={form.honey}
                onChange={onChange}
                className="hp"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="cv6-row">
                <div className="cv6-field">
                  <label htmlFor="cv6-name">Full Name *</label>
                  <input
                    id="cv6-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="e.g. Rahul Sharma"
                    required
                  />
                </div>

                <div className="cv6-field">
                  <label htmlFor="cv6-email">Business Email *</label>
                  <input
                    id="cv6-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <div className="cv6-field">
                <label htmlFor="cv6-topic">Project Scope / Primary Interest</label>
                <select
                  id="cv6-topic"
                  name="topic"
                  value={form.topic}
                  onChange={onChange}
                >
                  <option value="">Select a topic</option>
                  {topics.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cv6-field">
                <label htmlFor="cv6-message">Project Details &amp; Objectives *</label>
                <textarea
                  id="cv6-message"
                  rows="4"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us about your brand, current traffic/revenue, and what you aim to achieve..."
                  required
                />
              </div>

              <label className="cv6-check">
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                />
                <span>
                  I agree to be contacted regarding this growth inquiry and accept the{" "}
                  <Link to="/privacy-policy">privacy policy</Link>.
                </span>
              </label>

              {status.message && (
                <div className={`cv6-status-box ${status.type}`}>
                  {status.type === "error" ? (
                    <FaExclamationCircle />
                  ) : (
                    <FaCheckCircle />
                  )}
                  <span>{status.message}</span>
                </div>
              )}

              <button
                className="cv6-submit-btn"
                type="submit"
                disabled={loading}
              >
                <FaPaperPlane />
                <span>{loading ? "Sending Details…" : "Send Message"}</span>
              </button>
            </form>
          </motion.div>

          {/* RIGHT: DIRECT CONTACT SIDEBAR */}
          <motion.aside
            className="cv6-side"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="cv6-card cv6-side-card">
              <div className="side-card-top">
                <h3>Direct Contacts</h3>
                <p>
                  Prefer a direct line? Our strategists are available during business hours.
                </p>
              </div>

              <ul className="cv6-contact-list">
                <li>
                  <div className="icon-badge">
                    <FaEnvelope />
                  </div>
                  <div>
                    <span className="contact-kicker">Email Us</span>
                    <a href={`mailto:${brand.email}`} className="contact-link">
                      {brand.email}
                    </a>
                  </div>
                </li>

                <li>
                  <div className="icon-badge">
                    <FaPhoneAlt />
                  </div>
                  <div>
                    <span className="contact-kicker">Phone Support</span>
                    <a href={`tel:${brand.phonecall}`} className="contact-link">
                      {brand.phone}
                    </a>
                  </div>
                </li>

                <li>
                  <div className="icon-badge">
                    <FaClock />
                  </div>
                  <div>
                    <span className="contact-kicker">Operational Hours</span>
                    <div className="contact-text">{brand.hours}</div>
                  </div>
                </li>

                <li>
                  <div className="icon-badge">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <span className="contact-kicker">Location</span>
                    <div className="contact-text">{brand.address}</div>
                  </div>
                </li>
              </ul>

              <a
                className="cv6-whatsapp-btn"
                href={brand.whatsapp}
                target="_blank"
                rel="noreferrer"
              >
                <FaWhatsapp />
                <span>Instant WhatsApp Chat</span>
              </a>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
